<template>
  <div>
    <bar-chart
      :ref="chartRef"
      :chart-data="chartData"
      :options="chartOptions"
      :styles="chartStyles"
      :height="null"
      :width="null"
    />
    <div class="row chartChecks">
      <div
        v-if="!isConversational"
        class="col-sm-3"
      >
        <h5>Automated Scores (Highest)</h5>
        <div
          v-for="(metric) in metrics"
          v-if="metric.col === 1"
        >
          <label :for="metric.id">
            <input
              :id="metric.id"
              v-model="selectedMetrics"
              type="checkbox"
              :value="metric.id"
              @change="chartRef++"
            >
            {{ metric.label }}
          </label>
        </div>
      </div>
      <div
        v-if="showSpeakerRatings"
        class="col-sm-3"
      >
        <h5>Ratings (Highest Avg Scores)</h5>
        <div
          v-for="(metric) in metrics"
          v-if="metric.col === 2"
        >
          <label :for="metric.id">
            <input
              :id="metric.id"
              v-model="selectedMetrics"
              type="checkbox"
              :value="metric.id"
              @change="chartRef++"
            >
            {{ metric.label }}
          </label>
        </div>
      </div>
      <div class="col-sm-3">
        <h5>Ratings (Highest Avg Coach Scores)</h5>
        <div
          v-for="metric in metrics"
          v-if="metric.col === 3"
        >
          <label :for="metric.id">
            <input
              :id="metric.id"
              v-model="selectedMetrics"
              type="checkbox"
              :value="metric.id"
              @change="chartRef++"
            >
            {{ metric.label }}
          </label>
        </div>
      </div>

      <div class="col-sm-3">
        <h5>Comparison Report Settings</h5>
        <div>
          <label for="scoreType1">
            <input
              id="scoreType1"
              v-model="scoreType"
              type="radio"
              value="flaggedScores"
              @change="chartRef++"
            >
            Only Recordings Flagged for Coach Review
          </label>
        </div>
        <div>
          <label for="scoreType2">
            <input
              id="scoreType2"
              v-model="scoreType"
              type="radio"
              value="sharedScores"
              @change="chartRef++"
            >
            Only Recordings Shared with the Group
          </label>
        </div>
        <div>
          <label for="scoreType3">
            <input
              id="scoreType3"
              v-model="scoreType"
              type="radio"
              value="scores"
              @change="chartRef++"
            >
            All Recordings
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<style type="text/less" scoped>
  .chartChecks {
    margin: 20px 0;
  }
  label {
    font-size: 11px;
  }
  input {
    position: relative;
    top: 2px;
  }
</style>

<script>
import BarChart from '../specials/BarChart.js';
import util from '../utils/util';
import ScorecardMixin from '../specials/ScorecardMixin';

export default {
  components: {BarChart},
  mixins: [ScorecardMixin],
  props: ['assignment', 'group'],
  data() {
    return {
      scoreType: null,
      chartRef: 1,
      selectedMetrics: null,
      chartStyles:  {
        position: 'relative',
      },
      chartOptions: {
        legend: {
          align: 'start',
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              suggestedMax: 10,
            }
          }]
        },
        responsive: true,
        aspectRatio: util.isSmallViewport() ? 2 : 3,
        maintainAspectRatio: true,
      },
    };
  },
  computed: {
    isConversational() {
      return !!(this.assignment.criteria && this.assignment.criteria.algorithm &&
        this.assignment.criteria.algorithm.indexOf('conversational') !== -1);
    },
    showSpeakerRatings: {
      cache: false,
      get() {
        if (this.hasCustomScorecard && this.scorecard.coachOnlyScorecard) {
          return false;
        }
        return (this.group.speakerPrivacyLevel !== 4);
      }
    },
    metrics: {
      cache: false,
      get() {
        let metrics = [
          { label: 'Vocal Delivery', id: 'overallMax', suffix: ' (Automated)', col: 1},
          { label: 'Clarity', id: 'clarityMax', suffix: '', col: 1},
          { label: 'Strength of Opening', id: 'strengthOfOpeningMax', suffix: '', col: 1},
          { label: 'Captivating', id: 'captivatingMax', suffix: '', col: 1},
          { label: 'Confident', id: 'confidentMax', suffix: '', col: 1},
          { label: 'Authentic', id: 'authenticMax', suffix: '', col: 1},
          { label: 'Boring', id: 'boringMax', suffix: '', col: 1},
          { label: 'Arrogant', id: 'arrogantMax', suffix: '', col: 1},
        ];
        if (this.showSpeakerRatings) {
          metrics.push(
            { label: 'Vocal Delivery', preset: true, id: 'scoreDelivery', suffix: ' Rating', col: 2},
            { label: 'Organization and Content', preset: true, id: 'scoreOrganization', suffix: ' Rating', col: 2},
            { label: 'X-Factor', preset: true, id: 'scoreXfactor', suffix: ' Rating', col: 2},
            { label: 'Overall', preset: true, id: 'scoreOverall', suffix: ' Rating', col: 2}
          );
        }
        metrics.push(
          { label: 'Vocal Delivery', preset: true, id: 'scoreDeliveryCoach', suffix: ' Coach Rating', col: 3},
          { label: 'Organization and Content', preset: true, id: 'scoreOrganizationCoach', suffix: ' Coach Rating', col: 3},
          { label: 'X-Factor', preset: true, id: 'scoreXfactorCoach', suffix: ' Coach Rating', col: 3},
          { label: 'Overall', preset: true, id: 'scoreOverallCoach', suffix: ' Coach Rating', col: 3}
        );

        if (this.hasCustomScorecard) {
          this.scorecard.questions.forEach((rating) => {
            if (rating.preset) return;

            if (this.showSpeakerRatings) {
              const speakerRating = this._.cloneDeep(rating);
              speakerRating.suffix = ' Rating';
              speakerRating.col = 2;
              metrics.push(speakerRating);
            }

            const coachRating = this._.cloneDeep(rating);
            coachRating.id = coachRating.id + 'Coach';
            coachRating.suffix = ' Coach Rating';
            coachRating.col = 3;
            metrics.push(coachRating);
          });
        }
        return metrics;
      }
    },
    preferences: {
      cache: false,
      get() {
        return this.$store.getters.preferences;
      }
    },
    chartData: {
      cache: true,
      get() {
        if (this.selectedMetrics === null) {
          if (typeof this.preferences.assignmentComparisonScoreType !== 'undefined') {
            this.scoreType = this.preferences.assignmentComparisonScoreType;
          } else {
            this.scoreType = 'flaggedScores';
          }
          // We cannot load in the last used metrics for conversational because they could
          // be public speaking scores
          if (this.preferences.assignmentComparisonSelectedMetrics && !this.isConversational) {
            this.selectedMetrics = this.preferences.assignmentComparisonSelectedMetrics;
          } else {
            this.selectedMetrics = !this.isConversational ? ['overallMax'] : [];
            let hasVox = false;
            let hasCoachVox = false;
            this.assignment.members.forEach((m) => {
              if (m[this.scoreType].scoreDelivery) {
                hasVox = true;
              }
              if (m[this.scoreType].scoreDeliveryCoach) {
                hasCoachVox = true;
              }
            });
            if (hasCoachVox) {
              this.selectedMetrics.push('scoreDeliveryCoach');
            } else if (hasVox) {
              this.selectedMetrics.push('scoreDelivery');
            }
          }
        }

        // Remove any metrics that are present in preferences but no longer exist on the page
        this.selectedMetrics = this._.remove(
          this.selectedMetrics,
          (metric) => {
            let found = false;
            this.metrics.forEach((m) => {
              if (m.id === metric) found = true;
            });
            return found;
          }
        );

        const colors = ['rgba(39, 70, 124, 1.0)', 'rgba(239, 160, 66, 1.0)', 'rgba(129, 38, 102, 1.0)', 'rgba(49, 142, 187, 1.0)', 'rgba(211, 56, 47, 1.0)'];
        const datasets = [];
        const scores = this.selectedMetrics;
        const labels = [];
        let i = 0;
        const _members = this._.reverse(this._.sortBy(this.assignment.members, (m) => m[this.scoreType][scores[0]]));

        _members.forEach((member) => {
          labels.push(member.name);
        });

        scores.forEach((score) => {
          i++;
          const backgroundColor = colors[i % 5];
          let memData = {
            label: this._.find(this.metrics, {id: score})['label'] +
              this._.find(this.metrics, {id: score})['suffix'],
            backgroundColor,
            data: [],
          };
          _members.forEach((member) => {
            memData.data.push(member[this.scoreType][score]);
          });
          datasets.push(memData);
        });

        // Update the preferences store with the newly selected group
        this.$store.dispatch(
          'updatePreferences',
          Object.assign(
            this.preferences,
            {
              assignmentComparisonSelectedMetrics: this.selectedMetrics,
              assignmentComparisonScoreType: this.scoreType
            })
        );

        return {
          labels,
          datasets,
        };
      }
    },
  },
};
</script>